import { getSiteCatalog, getSiteReceivableOrders, receiveOnBehalf } from '@api/cm-sites';
import { getReceivableOrders, getVendorCatalog, type OverDeliveryDecision, postReceive } from '@api/vendor-portal';
import { getStoreCatalog, getStoreReceivableOrders, receiveIntoStore } from '@api/warehouse-ops';
import { Plus ,
    DownloadSimpleIcon,
    PlusIcon,
    TrashIcon,
    UploadSimpleIcon,
    WarningIcon,
} from '@phosphor-icons/react';

import PageHeader from '@components/common/PageHeader';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import usePersistentState from '@hooks/usePersistentState';
import useToaster from '@hooks/toaster/useToaster';
import {
    ActionIcon,
    Button,
    FileButton,
    Group,
    Modal,
    SegmentedControl,
    Select,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { DateInput, DatePickerInput } from '@mantine/dates';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { buildMaterialOptions } from '@components/containers/vendorPortal/materialOptions';
import { type OnBehalfOf } from '@components/containers/vendorPortal/portalContext';

import AddSupplierModal from './AddSupplierModal';
import styles from './ReceiveGrid.module.css';
import { receiveFieldClasses, useEnteredByName } from './receiveForm';
import {
    actionsFor,
    ADD_SUPPLIER_VALUE,
    blankRow,
    hydrateRow,
    isBlank,
    isOverOrderIssue,
    isTracked,
    keepAddSupplier,
    orderIssues,
    parseCsv,
    parsePastedBlock,
    QC_VERDICT_OPTIONS,
    quantityByItem,
    REJECTION_REASON_OPTIONS,
    resolveAgainstCatalog,
    rowIssues,
    SITE_REASONS,
    templateCsv,
    toLines,
    tracesAnything,
    type ReceiveRow,
} from './rows';
import { formatCount } from '@utils/currency';

/**
 * How many rows one import may bring.
 *
 * A delivery note does not have a thousand lines on it, and a file that does is a mistake
 * somebody wants to hear about rather than something to render. What is dropped is always
 * said out loud - a silent truncation reads as "it all came in".
 */
const MAX_IMPORT_ROWS = 500;

/** The Item column: never narrower than it used to be, never wider than two of the old one. */
const ITEM_COLUMN_MIN = 230;
const ITEM_COLUMN_MAX = 460;

const DRAFT_KEY = 'cm-receive-draft-v1';

/**
 * Suggested currencies, not a whitelist - the server accepts any three-letter code. Averages
 * group by currency rather than converting, so a note in dollars stays a dollar figure.
 */
const RECEIPT_CURRENCIES = ['INR', 'USD', 'CNY', 'EUR', 'GBP'];

interface Draft {
    savedAt: string;
    reference: string;
    receivedDate: string;
    /**
     * One currency for the whole note. A challan is a single document from a single supplier;
     * nobody bills line 1 in dollars and line 2 in rupees, so this belongs beside the
     * reference rather than as a column on every row.
     */
    currency: string;
    /**
     * The PO number or stock transfer code this delivery is received against. One per note,
     * like the challan: the rows are the items on it.
     */
    purchaseOrderId: string;
    rows: ReceiveRow[];
}

function emptyDraft(): Draft {
    return {
        savedAt: '',
        reference: '',
        receivedDate: '',
        currency: 'INR',
        purchaseOrderId: '',
        rows: [blankRow()],
    };
}

/**
 * A draft restored from disk, repaired into a draft this build understands.
 *
 * THE STORED VALUE IS NOT A `Draft`, whatever its type says. It was written by whichever version
 * of this screen the operator last had open, and `usePersistentState` hands it straight back with
 * no check - so a field added since is simply absent, and the first line of code to dereference
 * it throws. `unitCost` was added on 11 August and did exactly that.
 *
 * ROWS ARE WALKED INDIVIDUALLY, because a spread over the defaults would take the stored array
 * whole and repair nothing inside it - which is where the missing field actually was.
 *
 * A draft that is unreadable altogether - not an object, or with no rows - falls back to an empty
 * one rather than throwing, because a screen that will not open cannot be used to record the
 * delivery standing at the door.
 */
function hydrateDraft(stored: unknown): Draft {
    const fresh = emptyDraft();
    if (!stored || typeof stored !== 'object') return fresh;

    const draft = stored as Partial<Record<keyof Draft, unknown>>;
    const text = (value: unknown, fallback = '') =>
        typeof value === 'string' ? value : fallback;

    const rows = Array.isArray(draft.rows) ? draft.rows.map(hydrateRow) : [];

    return {
        savedAt: text(draft.savedAt),
        reference: text(draft.reference),
        receivedDate: text(draft.receivedDate),
        currency: text(draft.currency, 'INR') || 'INR',
        purchaseOrderId: text(draft.purchaseOrderId),
        // Never no rows at all: the grid is the screen, and an empty one cannot be typed into.
        rows: rows.length > 0 ? rows : [blankRow()],
    };
}

/** Whether a draft was already on disk when the screen opened, which is what the banner is about. */
/** Field labels on the design-system scale; shared with the finished-goods GRN. */
const fieldClasses = receiveFieldClasses;

function hadSavedDraft(): boolean {
    try {
        const raw = window.localStorage.getItem(DRAFT_KEY);
        if (!raw) return false;
        const parsed = JSON.parse(raw) as Draft;
        return (parsed.rows ?? []).some((row) => !isBlank(row));
    } catch {
        return false;
    }
}

function formatSavedAt(value: string): string {
    if (!value) return 'earlier';
    const when = new Date(value);
    if (Number.isNaN(when.getTime())) return 'earlier';
    return when.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Recording a delivery.
 *
 * A GRID, NOT A FORM. The thing being copied is a challan with ten lines on it, and a wizard
 * that asks for one line at a time turns a two-minute job into ten. Everything is typed, tabbed
 * through and submitted once.
 *
 * IT SURVIVES THE POWER GOING OUT. Every keystroke goes to localStorage, so a site that loses
 * the connection at row thirty finds row thirty waiting when it comes back rather than starting
 * again — which is the difference between a portal that gets used and a notebook that does.
 *
 * TWO LAYERS OF VALIDATION, ONE AUTHORITY. Rows are checked while they are typed so problems
 * surface next to the field that caused them, and the server checks again on submit and wins.
 * A submission lands whole or not at all, so a rejection means fixing what it names, never
 * working out which rows already went in.
 */
interface VendorReceiveContainerProps {
    /**
     * Set when the brand is keying a delivery a site reported. Everything about the grid is
     * identical — only which door it writes through, and which draft it restores, change.
     */
    onBehalfOf?: OnBehalfOf | null;
    /**
     * Rendered inside another screen's tab rather than as its own page.
     *
     * Suppresses the page header — a page header inside a page reads as a second screen —
     * and moves its submit button inline, because the button is the one part of that header
     * the form cannot do without.
     */
    embedded?: boolean;
    /**
     * A brand material store (Warehouse Ops) instead of a site. The grid is the same; it reads
     * and writes through the store's door, and the name field is who received the goods.
     */
    materialStoreId?: string;
    /** Called after a delivery is saved, for a host that closes or refreshes around the grid. */
    onSaved?: () => void;
}

const VendorReceiveContainer: FC<VendorReceiveContainerProps> = ({
    onBehalfOf: onBehalfOfProp,
    embedded = false,
    materialStoreId,
    onSaved,
}) => {
    // A store is keyed by the brand like a site, so everything below treats it as one.
    const onBehalfOf = useMemo(
        () => onBehalfOfProp ?? (materialStoreId ? { warehouseId: materialStoreId, reportedBy: '' } : null),
        [onBehalfOfProp, materialStoreId],
    );
    const { showToastSuccess, showToastError } = useToaster();
    const location = useLocation();
    const navigate = useNavigate();

    // Per site, so a coordinator working two sites in one morning cannot have one's half-typed
    // delivery note reappear under the other.
    const draftKey = onBehalfOf ? `${DRAFT_KEY}:${onBehalfOf.warehouseId}` : DRAFT_KEY;
    const [storedDraft, setDraft] = usePersistentState<Draft>(draftKey, emptyDraft());
    /*
     * Repaired on the way out of storage, every render, rather than once on mount - the key
     * changes when a coordinator switches site, and the draft that arrives with it needs the
     * same treatment as the one we started with.
     */
    const draft = useMemo(() => hydrateDraft(storedDraft), [storedDraft]);
    // Who is typing this: seeded once from the signed-in user, then whatever they make it.
    const [enteredBy, setEnteredBy] = useEnteredByName();
    const [restored, setRestored] = useState(hadSavedDraft);
    const [serverError, setServerError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addSupplierFor, setAddSupplierFor] = useState<{
        rowId: string;
        itemId: string;
        /** A vendor the row already names but the item is not mapped to - a pasted file's case. */
        vendorId?: string;
        /** What was typed into the picker before giving up on it. */
        search: string;
    } | null>(null);
    // What each row's supplier picker last had typed into it, so the form opens on that name.
    const supplierSearch = useRef(new Map<string, string>());

    const door = materialStoreId ? `store:${materialStoreId}` : onBehalfOf ? `site:${onBehalfOf.warehouseId}` : 'self';
    const { data: catalog, isLoading } = useQuery(
        [door, 'vendor-catalog'],
        () =>
            materialStoreId
                ? getStoreCatalog(materialStoreId)
                : onBehalfOf
                  ? getSiteCatalog(onBehalfOf.warehouseId)
                  : getVendorCatalog(),
        { staleTime: 5 * 60 * 1000 },
    );

    const rows = draft.rows;

    /*
     * AGAINST A PURCHASE ORDER OR A STOCK TRANSFER. A brand that receives against POs must pick
     * one first; any brand may pick a transfer the goods came on. Once picked, the rows can only
     * be its items, never more of one than it still owes. With nothing picked, a brand that
     * receives openly gets the grid exactly as it was.
     */
    const againstPo = catalog?.receivingMode === 'against_po';
    const queryClient = useQueryClient();
    const ordersKey = [door, 'receivable-orders'];
    const { data: openOrders = [], isLoading: ordersLoading } = useQuery(
        ordersKey,
        () =>
            materialStoreId
                ? getStoreReceivableOrders(materialStoreId)
                : onBehalfOf
                  ? getSiteReceivableOrders(onBehalfOf.warehouseId)
                  : getReceivableOrders(),
        { enabled: !!catalog },
    );
    // A brand that receives openly is offered only transfers: its POs never had to be named.
    const documents = againstPo ? openOrders : openOrders.filter((candidate) => candidate.kind === 'STN');
    const order = documents.find((candidate) => candidate.orderNumber === draft.purchaseOrderId);
    const onTransfer = order?.kind === 'STN';
    // The rows come from the picked order or transfer rather than being typed freely.
    const fromDocument = againstPo || !!order;
    /*
     * THE BRAND MAY KEEP MORE THAN IT ORDERED; A SITE MAY NOT. On the brand's own door - a site
     * keyed on its behalf, or one of its material stores - a quantity over the order is not a
     * mistake to fix but a question to answer: pay for the extra at the order's rate, or take
     * it free. The answer rides on the rows and the server amends the order in the same write.
     * A site's login, and any stock transfer, keep the hard stop.
     */
    const brandDecides = !!onBehalfOf && !!order && !onTransfer;
    const orderLineByItem = useMemo(
        () => new Map((order?.lines ?? []).map((line) => [line.itemId, line])),
        [order],
    );
    // Which items already have a row, keyed by the picks alone so typing a quantity does not
    // rebuild the grid's columns.
    const pickedKey = rows.map((row) => row.itemId).join('|');
    const pickedItems = useMemo(() => new Set(pickedKey.split('|').filter(Boolean)), [pickedKey]);

    /**
     * Rows asked for by whoever sent us here.
     *
     * A blocked declaration hands over what it was short of, so the exit lands on a grid that
     * already names the materials rather than on an empty one the person has to re-remember.
     * Appended, never over the draft: somebody may have been halfway through a delivery note
     * when they went to declare.
     *
     * Runs once per arrival. The ref is what stops a re-render from adding the same rows again,
     * since navigation state persists for as long as the entry does.
     */
    const prefilled = useRef(false);
    const prefillMaterialIds = (location.state as { prefillMaterialIds?: string[] } | null)
        ?.prefillMaterialIds;

    useEffect(() => {
        if (prefilled.current || !prefillMaterialIds?.length) return;
        prefilled.current = true;

        setDraft((current) => ({
            ...current,
            savedAt: new Date().toISOString(),
            rows: [
                ...current.rows.filter((row) => !isBlank(row)),
                ...prefillMaterialIds.map((itemId) => ({ ...blankRow(), itemId })),
                blankRow(),
            ],
        }));
    }, [prefillMaterialIds, setDraft]);

    const patch = (changes: Partial<Draft>) =>
        setDraft((current) => ({ ...current, ...changes, savedAt: new Date().toISOString() }));

    // All three update from the previous draft rather than from `rows` in scope. A cell renderer
    // outlives the render that created it — it is still mounted while the person types into it —
    // so reading rows from the closure would write yesterday's grid back over today's.
    const editRows = (change: (rows: ReceiveRow[]) => ReceiveRow[]) =>
        setDraft((current) => ({
            ...current,
            rows: change(current.rows),
            savedAt: new Date().toISOString(),
        }));

    const updateRow = (id: string, changes: Partial<ReceiveRow>) =>
        editRows((current) =>
            current.map((row) => (row.id === id ? { ...row, ...changes } : row)),
        );

    const addRow = () => editRows((current) => [...current, blankRow()]);

    const removeRow = (id: string) =>
        editRows((current) => {
            const next = current.filter((row) => row.id !== id);
            // Never leave nothing to type into.
            return next.length > 0 ? next : [blankRow()];
        });

    const issuesByRow = useMemo(() => {
        const byRow = new Map(rows.map((row) => [row.id, rowIssues(row, catalog)]));
        if (!fromDocument) return byRow;

        const unitOf = (itemId: string) =>
            catalog?.materials.find((material) => material.itemId === itemId)?.unit ?? '';
        const remaining = new Map(
            [...orderLineByItem.values()].map((line) => [line.itemId, line.remaining]),
        );
        for (const [rowId, issues] of orderIssues(rows, remaining, unitOf)) {
            // Over the order is a question on the brand's door, not a fault; the cell still says
            // how far over through the Remaining column.
            const kept = brandDecides ? issues.filter((issue) => !isOverOrderIssue(issue)) : issues;
            if (kept.length > 0) byRow.set(rowId, [...(byRow.get(rowId) ?? []), ...kept]);
        }
        return byRow;
    }, [rows, catalog, fromDocument, orderLineByItem, brandDecides]);

    // How far past the order each item is, so the Remaining cell can say it beside the number.
    const overByItem = useMemo(() => {
        const over = new Map<string, number>();
        if (!fromDocument) return over;
        for (const [itemId, total] of quantityByItem(rows)) {
            const line = orderLineByItem.get(itemId);
            if (line && total > line.remaining + 1e-6) over.set(itemId, total - line.remaining);
        }
        return over;
    }, [rows, fromDocument, orderLineByItem]);

    // What the brand has to answer for before the delivery can go in, one entry per item over.
    const overItems = useMemo(
        () =>
            [...overByItem].map(([itemId, over]) => {
                const line = orderLineByItem.get(itemId);
                const material = catalog?.materials.find((candidate) => candidate.itemId === itemId);
                return {
                    itemId,
                    name: material?.name ?? line?.description ?? itemId,
                    unit: material?.unit ?? '',
                    ordered: line?.ordered ?? 0,
                    remaining: line?.remaining ?? 0,
                    over,
                    unitPrice: line?.unitPrice ?? null,
                };
            }),
        [overByItem, orderLineByItem, catalog],
    );
    /** The dialog's answers while it is open, per item; null while it is not. */
    const [overPrompt, setOverPrompt] = useState<Record<string, OverDeliveryDecision> | null>(null);

    const filledRows = rows.filter((row) => !isBlank(row));
    const problemCount = [...issuesByRow.values()].filter((list) => list.length > 0).length;
    /*
     * EVERY PROBLEM SPELLED OUT UNDER THE GRID, not behind a hover. The icon at the row's end
     * was the only place the words lived, and people looking at a greyed-out Save button did
     * not think to point at it.
     */
    const problems = rows.flatMap((row, index) => {
        const issues = issuesByRow.get(row.id) ?? [];
        if (issues.length === 0) return [];
        const name =
            catalog?.materials.find((material) => material.itemId === row.itemId)?.name ??
            `Row ${index + 1}`;
        return [{ rowId: row.id, name, issues }];
    });
    const canSubmit =
        filledRows.length > 0 &&
        problemCount === 0 &&
        !isSubmitting &&
        (!againstPo || !!order) &&
        // Recording on a site's behalf without saying who reported it would file the
        // brand's typing as the site's own word.
        (!onBehalfOf || enteredBy.trim().length > 0);

    const showBatchColumns = tracesAnything(catalog);
    // The site's own door is handed no supplier and asks for none; the server attributes.
    const showSupplier = !catalog?.suppliersHidden;

    // Against a PO, only what is on it and still owed - the order is the list.
    const materialOptions = useMemo(
        () =>
            buildMaterialOptions(
                (catalog?.materials ?? []).filter(
                    (material) => !fromDocument || orderLineByItem.has(material.itemId),
                ),
            ),
        [catalog, fromDocument, orderLineByItem],
    );

    /**
     * The Item column fits its longest name.
     *
     * At 230px a third of a site's seasonings read "Seasoning_MAKHANA_KUR" - the part that
     * tells two apart is exactly the part that was cut. 12px medium runs about 7px a
     * character; the rest is the input's padding and chevron. Floored at the old width so a
     * short catalogue does not shrink the column, capped so one long name cannot eat the grid.
     */
    const itemColumnWidth = useMemo(() => {
        const longest = Math.max(0, ...materialOptions.map((option) => option.label.length));
        return Math.min(ITEM_COLUMN_MAX, Math.max(ITEM_COLUMN_MIN, longest * 7 + 56));
    }, [materialOptions]);

    const supplierOptions = useMemo(
        () =>
            (catalog?.suppliers ?? []).map((supplier) => ({
                value: supplier.vendorId,
                label: supplier.name,
            })),
        [catalog],
    );

    /**
     * Put parsed rows into the grid.
     *
     * THE ONE PLACE ROWS ARRIVE FROM OUTSIDE, whether they were pasted or uploaded. A file that
     * wrote to the database directly would be a second write path that drifts from this one —
     * every rule the typed rows pass, an imported row has to pass too, and the way to guarantee
     * that is for there to be no other way in.
     */
    const fillFrom = (parsed: ReceiveRow[], verb: string) => {
        if (parsed.length === 0) {
            showToastError({
                title: `Nothing to ${verb}`,
                subtitle: 'No rows were found in that.',
            });
            return;
        }

        const dropped = Math.max(0, parsed.length - MAX_IMPORT_ROWS);
        const capped = parsed.slice(0, MAX_IMPORT_ROWS);
        const { rows: resolved, unresolvedSuppliers } = resolveAgainstCatalog(capped, catalog);

        // Appended after what is already there, never over it: silently replacing rows somebody
        // had typed would be unrecoverable.
        editRows((current) => [...current.filter((row) => !isBlank(row)), ...resolved, blankRow()]);

        const notes = [
            dropped > 0 && `${dropped} row${dropped === 1 ? '' : 's'} past the first ${MAX_IMPORT_ROWS} were left out.`,
            unresolvedSuppliers > 0 &&
                `${unresolvedSuppliers} supplier${unresolvedSuppliers === 1 ? '' : 's'} could not be matched — pick them in the grid.`,
        ].filter(Boolean);

        showToastSuccess({
            title: `${resolved.length} row${resolved.length === 1 ? '' : 's'} added`,
            subtitle: notes.length > 0 ? notes.join(' ') : 'Check anything flagged before recording.',
        });
    };

    /**
     * Take a block pasted out of a spreadsheet.
     *
     * Only a BLOCK — something with a tab or a line break in it. A plain value pasted into one
     * cell is left entirely alone, because intercepting that would break the ordinary act of
     * copying a batch code from an email into the field the person is standing in.
     */
    const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
        // Against a PO or transfer the rows come from it, not from a spreadsheet.
        if (fromDocument) return;
        const text = event.clipboardData.getData('text/plain');
        if (!text || (!text.includes('\t') && !text.trim().includes('\n'))) return;

        const parsed = parsePastedBlock(text, showBatchColumns, showSupplier);
        if (parsed.length === 0) return;

        event.preventDefault();
        fillFrom(parsed, 'paste');
    };

    const handleFile = async (file: File | null) => {
        if (!file) return;
        try {
            fillFrom(parseCsv(await file.text(), showBatchColumns, showSupplier), 'import');
        } catch {
            showToastError({
                title: 'That file could not be read',
                subtitle: 'Save it as CSV and try again.',
            });
        }
    };

    /**
     * A template carrying this brand's columns — no batch fields for a brand that traces none.
     * Against a PO it comes with a row for every item the order still owes, already named.
     */
    const downloadTemplate = () => {
        const nameOf = new Map((catalog?.materials ?? []).map((m) => [m.itemId, m.name]));
        const items = (order?.lines ?? []).map((line) => nameOf.get(line.itemId) ?? line.description ?? line.itemId);
        const blob = new Blob([templateCsv(showBatchColumns, showSupplier, items)], {
            type: 'text/csv;charset=utf-8',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = order ? `receive-${order.orderNumber}.csv` : 'receive-template.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    /**
     * Pick the order a delivery came on. A different order is a different delivery, so the rows
     * typed against the last one go: they named its items, not this one's.
     */
    const pickOrder = (orderNumber: string) =>
        setDraft((current) => ({
            ...current,
            purchaseOrderId: orderNumber,
            rows:
                orderNumber === current.purchaseOrderId ? current.rows : [blankRow()],
            savedAt: new Date().toISOString(),
        }));

    /*
     * A transfer named in the link - the brand's "Mark received" on a stock transfer lands here
     * with it picked. Taken once the list has it, then dropped from the link so a refresh or a
     * later pick is not overridden.
     */
    const [searchParams, setSearchParams] = useSearchParams();
    const linkedTransfer = searchParams.get('stn');
    useEffect(() => {
        if (!linkedTransfer || !documents.some((candidate) => candidate.orderNumber === linkedTransfer)) return;
        if (draft.purchaseOrderId !== linkedTransfer) pickOrder(linkedTransfer);
        setSearchParams((params) => {
            params.delete('stn');
            return params;
        }, { replace: true });
        // Runs when the list arrives; pickOrder and the setter are stable enough for this.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedTransfer, documents.length]);

    /** The whole order arrived: a row for every item still owed, at what it still owes. */
    const addAllRemaining = () =>
        editRows((current) => {
            const kept = current.filter((row) => !isBlank(row));
            const present = new Set(kept.map((row) => row.itemId));
            const added = (order?.lines ?? [])
                .filter((line) => !present.has(line.itemId))
                .map((line) => ({
                    ...blankRow(),
                    itemId: line.itemId,
                    quantity: String(Math.round(line.remaining * 1000) / 1000),
                }));
            return [...kept, ...added, blankRow()];
        });

    const submit = async (rowsToSend: ReceiveRow[]) => {
        setIsSubmitting(true);
        setServerError(null);
        // Held before the draft is cleared: the toast names the order the delivery amended.
        const amendedOrder = order;
        try {
            const lines = toLines(rowsToSend, draft.currency || 'INR');
            const payload = {
                reference: draft.reference.trim() || undefined,
                receivedDate: draft.receivedDate.trim() || undefined,
                enteredByName: enteredBy.trim() || undefined,
                // Every line names the order or transfer picked above; the server checks it.
                lines: onTransfer
                    ? lines.map((line) => ({ ...line, purchaseOrderId: undefined, stnId: order?.stnId }))
                    : againstPo
                      ? lines.map((line) => ({ ...line, purchaseOrderId: draft.purchaseOrderId }))
                      : lines,
            };

            const result = materialStoreId
                ? await receiveIntoStore({ warehouseId: materialStoreId, reportedBy: enteredBy.trim() }, payload)
                : onBehalfOf
                ? // THE NAME IN THIS FORM IS THE NAME THAT IS RECORDED. It used to be
                  // rendered here and then thrown away, because provenance came from a
                  // banner on the page behind — so the brand saw two name boxes and only
                  // the other one counted. The banner is gone; this field is the reporter.
                  await receiveOnBehalf(
                      { ...onBehalfOf, reportedBy: enteredBy.trim() },
                      payload,
                  )
                : await postReceive(payload);

            const kept = result.receipt?.overDeliveries ?? [];
            const keptNote = kept
                .map(
                    (item) =>
                        `${formatCount(item.quantity)} ${item.unit} ${item.itemName} over the order kept ${item.decision === 'FREE' ? 'free' : 'at the order rate'}`
                            .replace(/\s+/g, ' '),
                )
                .join('; ');
            showToastSuccess({
                title: `Saved as ${result.grnNumber}`,
                subtitle: `${result.eventIds.length} line${result.eventIds.length === 1 ? '' : 's'} recorded. Stock at this site is updated.${
                    kept.length > 0 && amendedOrder ? ` ${keptNote}; a line for it is on ${amendedOrder.orderNumber}.` : ''
                }`,
                // The vendor holds the order as issued. Offered, not done: the challan and the
                // invoice are often all the paper anyone wants.
                ...(kept.length > 0 && amendedOrder
                    ? {
                          action: {
                              children: `Send updated PO${amendedOrder.vendorName ? ` to ${amendedOrder.vendorName}` : ''}`,
                              onClick: () =>
                                  navigate('/orders', {
                                      state: { openOrderNumber: amendedOrder.orderNumber, send: true },
                                  }),
                          },
                      }
                    : {}),
            });
            if (order) void queryClient.invalidateQueries(ordersKey);

            // The challan is spent; the person is not. Their name is what they will want
            // pre-filled tomorrow, and re-typing it every day is how it stops being accurate.
            setDraft({ ...emptyDraft(), savedAt: new Date().toISOString() });
            setRestored(false);
            onSaved?.();
        } catch (err: any) {
            /*
             * A REJECTION FROM THE SERVER AND A CRASH IN HERE ARE DIFFERENT THINGS, and were
             * being shown identically. The server's message is written for the person reading
             * it - "Batch CFP-1008 already exists at this site" - and belongs on screen. A
             * TypeError is not: an operator once met "Cannot read properties of undefined
             * (reading 'trim')" on pressing the button, which tells them nothing they can act on
             * and reads like their delivery was rejected.
             *
             * Anything the API returned still passes through untouched; only a genuine
             * client-side fault is translated, and it says what to do about it.
             */
            const fromServer = err?.error?.message ?? err?.response?.message;
            const isClientFault = !fromServer && err instanceof TypeError;

            setServerError(
                fromServer
                    ?? (isClientFault
                        ? 'This delivery could not be read - it may have been saved by an older '
                          + 'version of this page. Use Clear to start it again.'
                        : (err?.message ?? 'The delivery could not be recorded.')),
            );
            showToastError({
                title: 'Nothing was recorded',
                subtitle: 'No part of this delivery went in. Fix what is flagged and submit again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = () => {
        // The brand answers for the extra before anything is written. Nothing is saved yet.
        if (brandDecides && overItems.length > 0) {
            setOverPrompt(Object.fromEntries(overItems.map((item) => [item.itemId, 'PAY' as OverDeliveryDecision])));
            return;
        }
        void submit(rows);
    };

    const confirmOverDelivery = () => {
        if (!overPrompt) return;
        const decided = rows.map((row) =>
            overPrompt[row.itemId] ? { ...row, overDeliveryDecision: overPrompt[row.itemId] } : row,
        );
        setOverPrompt(null);
        editRows(() => decided);
        void submit(decided);
    };

    const columns = useMemo<DataSheetColumn<ReceiveRow>[]>(() => {
        const unitFor = (itemId: string) =>
            catalog?.materials.find((material) => material.itemId === itemId)?.unit ?? '';

        const base: DataSheetColumn<ReceiveRow>[] = [
            {
                key: 'itemId',
                header: 'Item',
                width: itemColumnWidth,
                sticky: true,
                render: (_value, row) => (
                    <Select
                        size="xs"
                        classNames={{ input: styles.cellInput, dropdown: styles.itemDropdown }}
                        placeholder={isLoading ? 'Loading…' : 'Pick an item'}
                        data={
                            fromDocument
                                ? // One row per item of the order: a second row for the same
                                  // item would only split one figure in two.
                                  materialOptions.map((option) => ({
                                      ...option,
                                      disabled:
                                          option.value !== row.itemId &&
                                          pickedItems.has(option.value),
                                  }))
                                : materialOptions
                        }
                        value={row.itemId || null}
                        onChange={(value) => updateRow(row.id, { itemId: value ?? '' })}
                        // The whole name on hover, for the day a name outgrows even the fitted column.
                        title={materialOptions.find((option) => option.value === row.itemId)?.label}
                        // The list sizes itself to its longest name rather than to the column, and
                        // hangs off the box's left edge, so every option reads on one line.
                        comboboxProps={{ width: 'max-content', position: 'bottom-start' }}
                        searchable
                        nothingFoundMessage={
                            fromDocument
                                ? `Not on this ${onTransfer ? 'stock transfer' : 'purchase order'}`
                                : 'No material of this site matches'
                        }
                        disabled={
                            isLoading || materialOptions.length === 0 || (againstPo && !order)
                        }
                        error={issuesByRow.get(row.id)?.some((i) => i.includes('item'))}
                    />
                ),
            },
            {
                key: 'quantity',
                header: 'Quantity',
                width: 130,
                // Pinned beside the item: it is the cell most problems are about, and it used to
                // scroll away under the item column.
                sticky: true,
                render: (_value, row) => (
                    <Group gap={6} wrap="nowrap">
                        <TextInput
                            size="xs"
                            classNames={{ input: styles.cellInput }}
                            placeholder="0"
                            value={row.quantity}
                            onChange={(e) => updateRow(row.id, { quantity: e.currentTarget.value })}
                            w={70}
                            error={issuesByRow.get(row.id)?.some((i) => i.includes('uantity'))}
                        />
                        <Text fz={12} c={neutralColors.textMuted}>
                            {unitFor(row.itemId)}
                        </Text>
                    </Group>
                ),
            },
            ...(fromDocument
                ? ([
                      // A transfer owes what was dispatched, not what was ordered.
                      ['ordered', onTransfer ? 'Dispatched' : 'Ordered'],
                      ['received', 'Received so far'],
                      ['remaining', 'Remaining'],
                  ] as const).map(
                      ([field, header]): DataSheetColumn<ReceiveRow> => ({
                          key: `_order_${field}`,
                          header,
                          width: field === 'remaining' ? 150 : field === 'received' ? 120 : 100,
                          csvValue: () => '',
                          render: (_value, row) => {
                              const line = orderLineByItem.get(row.itemId);
                              const over = field === 'remaining' ? overByItem.get(row.itemId) : undefined;
                              if (line && over !== undefined) {
                                  return (
                                      <Text fz={13} ta="right" fw={600} c={semanticColors.critical.dark}>
                                          {`${formatCount(line.remaining)} ${unitFor(row.itemId)} · ${formatCount(over)} over`}
                                      </Text>
                                  );
                              }
                              return (
                                  <Text
                                      fz={13}
                                      ta="right"
                                      fw={field === 'remaining' ? 600 : 400}
                                      c={line ? neutralColors.textPrimary : neutralColors.textMuted}
                                  >
                                      {line ? `${formatCount(line[field])} ${unitFor(row.itemId)}`.trim() : '—'}
                                  </Text>
                              );
                          },
                      }),
                  )
                : []),
            /*
             * THE VERDICT, ON EVERY GRID. QC used to be a tick only the brand could set and the
             * site's stock was born quarantined; it now happens once, at the gate, and the site
             * says what it found. No default, on purpose: "received 5,000" is not "approved
             * 5,000", and a row that has not said cannot be saved.
             */
            {
                key: 'qcVerdict',
                header: 'QC verdict',
                width: 150,
                render: (_value, row) => (
                    <Select
                        comboboxProps={{ position: 'bottom-start' }}
                        size="xs"
                        classNames={{ input: styles.cellInput }}
                        placeholder="Pick a verdict…"
                        data={QC_VERDICT_OPTIONS}
                        value={row.qcVerdict || null}
                        onChange={(value) => updateRow(row.id, { qcVerdict: value ?? '' })}
                        error={issuesByRow.get(row.id)?.some((i) => i.includes('verdict'))}
                    />
                ),
                csvValue: (_value: unknown, row: ReceiveRow) => row.qcVerdict,
            },
            {
                // Asked only when the verdict needs a count; locked to what was received when
                // everything was refused, so the two cannot be typed to disagree.
                key: 'rejectedQuantity',
                header: 'Rejected',
                width: 90,
                render: (_value, row) =>
                    row.qcVerdict === 'SOME_REJECTED' ? (
                        <TextInput
                            size="xs"
                            classNames={{ input: styles.cellInput }}
                            placeholder="0"
                            value={row.rejectedQuantity}
                            onChange={(e) =>
                                updateRow(row.id, { rejectedQuantity: e.currentTarget.value })
                            }
                            error={issuesByRow.get(row.id)?.some((i) => i.includes('Rejected'))}
                        />
                    ) : (
                        <Text fz={12} c={neutralColors.textMuted} ta="right">
                            {row.qcVerdict === 'ALL_REJECTED' ? row.quantity : '—'}
                        </Text>
                    ),
                csvValue: (_value: unknown, row: ReceiveRow) => row.rejectedQuantity,
            },
            {
                // A fixed list rather than free text - the category a report can count. What
                // actually happened ("bent labels, top two rows") belongs in Notes, and only
                // the two "Other" reasons insist on it.
                key: 'rejectionReason',
                header: 'Reason',
                width: 190,
                render: (_value, row) =>
                    row.qcVerdict === 'SOME_REJECTED' || row.qcVerdict === 'ALL_REJECTED' ? (
                        <Select
                            comboboxProps={{ position: 'bottom-start' }}
                            size="xs"
                            classNames={{ input: styles.cellInput }}
                            placeholder="Why?"
                            data={REJECTION_REASON_OPTIONS}
                            value={row.rejectionReason || null}
                            onChange={(value) =>
                                updateRow(row.id, {
                                    rejectionReason: value ?? '',
                                    // A site reason cannot keep a Return picked under another one.
                                    ...(row.rejectionAction === 'RETURN' && SITE_REASONS.has(value ?? '')
                                        ? { rejectionAction: '' }
                                        : {}),
                                })
                            }
                            error={issuesByRow.get(row.id)?.some((i) => i.includes('reason'))}
                        />
                    ) : (
                        <Text fz={12} c={neutralColors.textMuted}>—</Text>
                    ),
                csvValue: (_value: unknown, row: ReceiveRow) => row.rejectionReason,
            },
            {
                // Where the refused stock goes, asked on its own and never defaulted: a missed
                // pick holds the row rather than quietly writing stock off that is still here.
                key: 'rejectionAction',
                header: 'Action',
                width: 150,
                render: (_value, row) =>
                    row.qcVerdict === 'SOME_REJECTED' || row.qcVerdict === 'ALL_REJECTED' ? (
                        <Select
                            comboboxProps={{ position: 'bottom-start' }}
                            size="xs"
                            classNames={{ input: styles.cellInput }}
                            placeholder="Then?"
                            data={actionsFor(row.rejectionReason, onTransfer).map((option) => ({ ...option }))}
                            value={row.rejectionAction || null}
                            onChange={(value) => updateRow(row.id, { rejectionAction: value ?? '' })}
                            error={issuesByRow.get(row.id)?.some((i) => i.includes('rejected stock') || i.includes('supplier —'))}
                        />
                    ) : (
                        <Text fz={12} c={neutralColors.textMuted}>—</Text>
                    ),
                csvValue: (_value: unknown, row: ReceiveRow) => row.rejectionAction,
            },
            {
                // Never typed, so it can never be copied across by reflex: what lands in stock is
                // what was received less what was refused, and the row shows it.
                key: 'approved',
                header: 'Approved',
                width: 90,
                render: (_value, row) => {
                    const received = Number(row.quantity);
                    const rejected =
                        row.qcVerdict === 'ALL_REJECTED'
                            ? received
                            : row.qcVerdict === 'SOME_REJECTED'
                              ? Number(row.rejectedQuantity) || 0
                              : 0;
                    const approved = received - rejected;
                    const known = row.qcVerdict !== '' && Number.isFinite(approved);
                    // Not green while the row cannot be saved: nothing is approved yet.
                    const blocked = (issuesByRow.get(row.id)?.length ?? 0) > 0;
                    return (
                        <Text
                            fz={13}
                            fw={600}
                            ta="right"
                            c={
                                !known || blocked
                                    ? neutralColors.textMuted
                                    : approved > 0
                                      ? semanticColors.positive.dark
                                      : semanticColors.critical.dark
                            }
                        >
                            {known ? formatCount(approved) : '—'}
                        </Text>
                    );
                },
                csvValue: () => '',
            },
        ];

        const batchColumns: DataSheetColumn<ReceiveRow>[] = [
            {
                key: 'batchCode',
                header: 'Batch',
                width: 150,
                render: (_value, row) => {
                    const material = catalog?.materials.find((m) => m.itemId === row.itemId);
                    const required = !!material && isTracked(catalog, material);
                    return (
                        <TextInput
                            size="xs"
                            classNames={{ input: styles.cellInput }}
                            // Deliberately blank rather than pre-filled: this is the one field
                            // that must match what is physically printed on the goods. The red
                            // border alone says it is required: a placeholder here read as a
                            // rejected value, and a tag beside the box looked like one too.
                            placeholder={required ? '' : 'Optional'}
                            value={row.batchCode}
                            onChange={(e) =>
                                updateRow(row.id, { batchCode: e.currentTarget.value })
                            }
                            error={required && !row.batchCode.trim() && !isBlank(row)}
                        />
                    );
                },
            },
            {
                key: 'supplierId',
                header: 'Supplier',
                width: 190,
                render: (_value, row) => {
                    const material = catalog?.materials.find((m) => m.itemId === row.itemId);
                    // Only who the brand has mapped to supply THIS item, never the whole vendor
                    // list: that list holds the brand's manufacturers, freight partners and
                    // head office too, and none of them sent the seasoning. Empty until an
                    // item is picked, and empty when nobody is mapped — the row's own issue
                    // text says which.
                    const options = supplierOptions.filter((option) =>
                        material?.supplierIds.includes(option.value),
                    );
                    const unmapped =
                        !!row.supplierId && !material?.supplierIds.includes(row.supplierId);
                    // Named by a pasted file but not mapped: offered as one step, not a search.
                    const unmappedName = unmapped
                        ? supplierOptions.find((option) => option.value === row.supplierId)?.label
                        : undefined;
                    // Required from the moment a traced item is picked, alongside the batch
                    // box, rather than only once a code is typed: the two are one requirement
                    // (a code is only unique to whoever printed it) and light up together.
                    const required = !!material && isTracked(catalog, material);
                    return (
                        <Select
                            comboboxProps={{ position: 'bottom-start' }}
                            size="xs"
                            classNames={{ input: styles.cellInput }}
                            placeholder={material ? 'Who sent it' : 'Pick an item first'}
                            // Brand side only: who the brand buys from is the brand's to add.
                            data={
                                onBehalfOf
                                    ? [
                                          ...options,
                                          {
                                              value: ADD_SUPPLIER_VALUE,
                                              label: unmappedName
                                                  ? `+ Map ${unmappedName} to this item`
                                                  : '+ Add supplier',
                                          },
                                      ]
                                    : options
                            }
                            filter={keepAddSupplier}
                            value={row.supplierId || null}
                            onSearchChange={(text) => {
                                // Picking the action writes its own label into the search box.
                                if (!text.startsWith('+ ')) supplierSearch.current.set(row.id, text);
                            }}
                            onChange={(value) => {
                                if (value !== ADD_SUPPLIER_VALUE) {
                                    updateRow(row.id, { supplierId: value ?? '' });
                                    return;
                                }
                                const typed = supplierSearch.current.get(row.id) ?? '';
                                setAddSupplierFor({
                                    rowId: row.id,
                                    itemId: row.itemId,
                                    vendorId: unmappedName ? row.supplierId : undefined,
                                    // A mapped supplier's own name in the box is a selection, not a search.
                                    search: options.some((option) => option.label === typed) ? '' : typed,
                                });
                            }}
                            searchable
                            clearable
                            disabled={!material}
                            nothingFoundMessage="Not mapped as a supplier of this item"
                            error={
                                ((required || !!row.batchCode.trim()) &&
                                    !row.supplierId &&
                                    !isBlank(row)) ||
                                unmapped
                            }
                        />
                    );
                },
            },
            {
                key: 'manufacturingDate',
                header: 'Manufactured',
                width: 130,
                render: (_value, row) => (
                    // DateInput, not DatePickerInput: this grid is filled by tabbing and
                    // pasting, and a picker that renders as a BUTTON would take that away.
                    // This is a real text input that also drops a calendar, so a date can be
                    // typed at speed or picked when somebody is reading it off a drum.
                    <DateInput
                        size="xs"
                        classNames={{ input: styles.cellInput }}
                        placeholder="YYYY-MM-DD"
                        valueFormat="YYYY-MM-DD"
                        value={row.manufacturingDate || null}
                        onChange={(value) =>
                            updateRow(row.id, { manufacturingDate: value ?? '' })
                        }
                        clearable
                    />
                ),
            },
            {
                key: 'expiryDate',
                header: 'Expires',
                width: 130,
                render: (_value, row) => (
                    <DateInput
                        size="xs"
                        classNames={{ input: styles.cellInput }}
                        placeholder="YYYY-MM-DD"
                        valueFormat="YYYY-MM-DD"
                        value={row.expiryDate || null}
                        onChange={(value) => updateRow(row.id, { expiryDate: value ?? '' })}
                        clearable
                    />
                ),
            },
        ];

        const tail: DataSheetColumn<ReceiveRow>[] = [
            // Against a PO or transfer it is picked once, above the grid, for the whole note.
            ...(fromDocument ? [] : [{
                key: 'purchaseOrderId',
                header: 'PO',
                width: 140,
                render: (_value, row) => (
                    <TextInput
                        size="xs"
                        classNames={{ input: styles.cellInput }}
                        placeholder="If known"
                        value={row.purchaseOrderId}
                        onChange={(e) =>
                            updateRow(row.id, { purchaseOrderId: e.currentTarget.value })
                        }
                    />
                ),
            } as DataSheetColumn<ReceiveRow>]),
            {
                key: 'notes',
                header: 'Notes',
                width: 170,
                render: (_value, row) => (
                    <TextInput
                        size="xs"
                        classNames={{ input: styles.cellInput }}
                        value={row.notes}
                        onChange={(e) => updateRow(row.id, { notes: e.currentTarget.value })}
                    />
                ),
            },
            // Cost is the brand's commercial data, so the column exists only when the brand is
            // the one keying. The manufacturer's own grid never renders it, and the server
            // ignores the field on that path even if it somehow arrived.
            ...(onBehalfOf
                ? [{
                    key: 'unitCost',
                    header: 'Rate (excl. GST)',
                    width: 140,
                    render: (_value: unknown, row: ReceiveRow) => (
                        <TextInput
                            size="xs"
                            classNames={{ input: styles.cellInput }}
                            placeholder="Per unit"
                            value={row.unitCost}
                            onChange={(e) =>
                                updateRow(row.id, { unitCost: e.currentTarget.value })
                            }
                        />
                    ),
                } as DataSheetColumn<ReceiveRow>]
                : []),
            {
                key: '_issues',
                header: '',
                width: 90,
                csvValue: () => '',
                render: (_value, row) => {
                    const issues = issuesByRow.get(row.id) ?? [];
                    return (
                        <Group gap={4} wrap="nowrap" justify="flex-end">
                            {issues.length > 0 && (
                                <Tooltip label={issues.join(' ')} multiline w={280}>
                                    <WarningIcon
                                        size={16}
                                        weight="fill"
                                        color={semanticColors.critical.primary}
                                    />
                                </Tooltip>
                            )}
                            <ActionIcon
                                size="sm"
                                variant="subtle"
                                aria-label="Remove row"
                                onClick={() => removeRow(row.id)}
                            >
                                <TrashIcon size={14} />
                            </ActionIcon>
                        </Group>
                    );
                },
            },
        ];

        const traced = showSupplier
            ? batchColumns
            : batchColumns.filter((column) => column.key !== 'supplierId');
        return showBatchColumns ? [...base, ...traced, ...tail] : [...base, ...tail];
        // Deliberately NOT keyed on `rows`: the renderers read each row from the argument they
        // are handed and write through the functional updaters above, so rebuilding the column
        // set on every keystroke would only churn the grid for no gain.
    }, [catalog, isLoading, materialOptions, itemColumnWidth, supplierOptions, issuesByRow, showBatchColumns, showSupplier, onBehalfOf, againstPo, fromDocument, onTransfer, order, orderLineByItem, overByItem, pickedItems]);

    const addSupplierMaterial = catalog?.materials.find(
        (material) => material.itemId === addSupplierFor?.itemId,
    );

    return (
        <div
            className={
                embedded
                    ? // Already inside a page that has its own padding; repeating page-shell
                      // here indents the form from a gutter it is not next to.
                      'flex w-full flex-col gap-4'
                    : 'flex h-full w-full flex-col gap-6 page-shell'
            }
            onPaste={handlePaste}
        >
            {embedded ? null : (
                <PageHeader
                    icon="📥"
                    title="Receive"
                    subtitle="Record material arriving at this site, one delivery note at a time"
                    actions={
                        /* The question after recording a delivery is "did it land?" -
                           answered by the RM/PM balances, one click away. */
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            onClick={() => navigate('/vendor/rm-pm')}
                        >
                            View RM/PM
                        </Button>
                    }
                />
            )}

            {restored && (
                <div className={styles.banner}>
                    <div className={styles.bannerText}>
                        <Text fz={13} fw={600} c={neutralColors.textPrimary}>
                            Picked up where you left off
                        </Text>
                        <Text fz={12} c={neutralColors.textSecondary}>
                            These rows were saved on this computer at {formatSavedAt(draft.savedAt)}{' '}
                            and have not been submitted.
                        </Text>
                    </div>
                    <Group gap={8}>
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            size="xs"
                            onClick={() => {
                                setDraft(emptyDraft());
                                setRestored(false);
                            }}
                        >
                            Discard
                        </Button>
                        <Button
                            variant={ButtonVariant.SUBTLE}
                            size="xs"
                            onClick={() => setRestored(false)}
                        >
                            Keep them
                        </Button>
                    </Group>
                </div>
            )}

            {serverError && (
                <div className={`${styles.banner} ${styles.bannerCritical}`}>
                    <div className={styles.bannerText}>
                        <Text fz={13} fw={600} c={neutralColors.textPrimary}>
                            Nothing was recorded
                        </Text>
                        <Text fz={12} c={neutralColors.textSecondary}>
                            {serverError}
                        </Text>
                    </div>
                </div>
            )}

            <Group gap={16} align="flex-end">
                {(againstPo || documents.length > 0) && (
                    <Select
                        comboboxProps={{ position: 'bottom-start' }}
                        classNames={fieldClasses}
                        label="Received against"
                        description={
                            onTransfer
                                ? `From ${order?.fromNodeName ?? 'the brand warehouse'} (stock transfer)`
                                : order?.vendorName
                                  ? `Supplier: ${order.vendorName} (from the PO)`
                                  : againstPo
                                    ? 'The PO or stock transfer the goods came on'
                                    : 'The stock transfer the goods came on, if any'
                        }
                        placeholder={ordersLoading ? 'Loading…' : againstPo ? 'Pick the PO or transfer' : 'Optional'}
                        // One list, headed by kind, so nobody has to say which kind first.
                        data={[
                            {
                                group: 'Purchase orders',
                                items: documents.filter((candidate) => candidate.kind !== 'STN').map((candidate) => ({
                                    value: candidate.orderNumber,
                                    label: candidate.vendorName
                                        ? `${candidate.orderNumber} · ${candidate.vendorName}`
                                        : candidate.orderNumber,
                                })),
                            },
                            {
                                group: 'Stock transfers',
                                items: documents.filter((candidate) => candidate.kind === 'STN').map((candidate) => ({
                                    value: candidate.orderNumber,
                                    label: candidate.fromNodeName
                                        ? `${candidate.orderNumber} · from ${candidate.fromNodeName}`
                                        : candidate.orderNumber,
                                })),
                            },
                        ].filter((group) => group.items.length > 0)}
                        value={order ? draft.purchaseOrderId : null}
                        onChange={(value) => pickOrder(value ?? '')}
                        searchable
                        clearable={!againstPo}
                        nothingFoundMessage="Nothing open with anything left to receive"
                        withAsterisk={againstPo}
                        w={320}
                    />
                )}
                <TextInput
                    classNames={fieldClasses}
                    label="Delivery note"
                    description="The challan it came on"
                    placeholder="CHLN-1234"
                    value={draft.reference}
                    onChange={(e) => patch({ reference: e.currentTarget.value })}
                    w={220}
                />
                <DatePickerInput
                    classNames={fieldClasses}
                    label="Received on"
                    description="Leave blank for today"
                    placeholder="Today"
                    valueFormat="DD MMM YYYY"
                    value={draft.receivedDate || null}
                    onChange={(value) => patch({ receivedDate: value ?? '' })}
                    clearable
                    w={190}
                />
                <TextInput
                    classNames={fieldClasses}
                    label={materialStoreId ? 'Received by' : 'Entered by'}
                    description={materialStoreId ? 'Who received the goods' : 'Who is entering this'}
                    placeholder="Your name"
                    value={enteredBy}
                    onChange={(e) => setEnteredBy(e.currentTarget.value)}
                    w={240}
                    withAsterisk={!!onBehalfOf}
                />
                {onBehalfOf && (
                    <Select
                        comboboxProps={{ position: 'bottom-start' }}
                        classNames={fieldClasses}
                        label="Currency"
                        description="Of the rates below"
                        data={RECEIPT_CURRENCIES}
                        value={draft.currency || 'INR'}
                        onChange={(value) => patch({ currency: value ?? 'INR' })}
                        allowDeselect={false}
                        w={130}
                    />
                )}
            </Group>

            {/* A site whose products have no bill of materials yet should be told that, not
                handed an empty dropdown. The list is deliberately not widened: the fix is the
                brand's, and naming it turns a dead end into a thing to go and ask for. */}
            {catalog && catalog.materials.length === 0 && (
                <Text fz={13} c={semanticColors.warning.dark}>
                    Nothing is on the bill of materials for what this site makes yet, so there is
                    nothing it can receive. The brand needs to add the RM and PM to those
                    products&apos; BOM before a delivery can be recorded here.
                </Text>
            )}

            {againstPo && !order && !ordersLoading && (
                <Text fz={13} c={neutralColors.textSecondary}>
                    {documents.length === 0
                        ? 'There is no open purchase order or stock transfer with anything left to receive at this site, so there is nothing to record against yet.'
                        : 'Pick the purchase order or stock transfer this delivery came on. Only its items, and only what it still owes, can be received.'}
                </Text>
            )}

            {/* Named before the grid, on the header and on the button: the person keying is
                giving a QC verdict, and should know it before the first row, not after. */}
            <Text fz={13} fw={500} c={neutralColors.textPrimary}>
                Recording this delivery is this site&apos;s QC. Every line needs a verdict before it
                can be saved — what you approve here goes straight into usable stock.
            </Text>

            <DataSheet<ReceiveRow>
                data={rows}
                columns={columns}
                title="Delivery lines"
                subtitle={
                    problemCount > 0
                        ? undefined
                        : filledRows.length === 0
                          ? 'One row per line on the challan. Pick an item to begin, or import a file.'
                          : `${filledRows.length} line${filledRows.length === 1 ? '' : 's'} ready`
                }
                getRowKey={(row) => row.id}
                getRowClassName={(row) =>
                    (issuesByRow.get(row.id)?.length ?? 0) > 0 ? styles.rowProblem : ''
                }
                // Tall enough for the inputs to sit in without clipping.
                rowHeight={48}
                maxHeight="auto"
                toolbarControls={
                    <Group gap={8} className={styles.toolbar}>
                        {problemCount > 0 && (
                            <Text fz={12} fw={600} c={semanticColors.critical.dark}>
                                {problemCount} row{problemCount === 1 ? '' : 's'} to fix
                            </Text>
                        )}
                        {/* Import lives here, beside the grid it fills, rather than as a tab of
                            its own — it is a way of typing, not a separate thing to do. Against
                            a PO it waits for the order: the template is that order's lines, and
                            an imported row is checked against what it still owes. */}
                        {fromDocument && (
                            <Button
                                variant={ButtonVariant.DEFAULT}
                                size="xs"
                                onClick={addAllRemaining}
                                disabled={!order}
                            >
                                Add all remaining items
                            </Button>
                        )}
                        {(!againstPo || order) && (
                            <>
                                <Button
                                    variant={ButtonVariant.SUBTLE}
                                    size="xs"
                                    leftSection={<DownloadSimpleIcon size={14} />}
                                    onClick={downloadTemplate}
                                >
                                    Template
                                </Button>
                                <FileButton onChange={handleFile} accept=".csv,text/csv">
                                    {(props) => (
                                        <Button
                                            {...props}
                                            variant={ButtonVariant.DEFAULT}
                                            size="xs"
                                            leftSection={<UploadSimpleIcon size={14} />}
                                        >
                                            Import CSV
                                        </Button>
                                    )}
                                </FileButton>
                            </>
                        )}
                        <Button
                            variant={ButtonVariant.DEFAULT}
                            size="xs"
                            leftSection={<PlusIcon size={14} />}
                            onClick={addRow}
                            disabled={againstPo && !order}
                        >
                            Add row
                        </Button>
                    </Group>
                }
            />

            {problems.length > 0 && (
                <div className={`${styles.banner} ${styles.bannerCritical} ${styles.problemList}`}>
                    <div className={styles.bannerText}>
                        <Text fz={13} fw={600} c={semanticColors.critical.dark}>
                            {problems.length} row{problems.length === 1 ? '' : 's'} to fix before this
                            delivery can be saved
                        </Text>
                        {problems.map((problem) => (
                            <Text key={problem.rowId} fz={12} c={neutralColors.textPrimary}>
                                <Text span fz={12} fw={600}>
                                    {problem.name}:
                                </Text>{' '}
                                {problem.issues.join(' ')}
                            </Text>
                        ))}
                    </div>
                </div>
            )}

            <div className={styles.submitBar}>
                <Text fz={12} c={neutralColors.textMuted}>
                    Saved on this computer as you type — paste or import a file if you have one.
                    Imported rows are checked exactly like typed ones. The whole delivery goes in
                    together, or none of it does.
                </Text>
                {/*
                  * BELOW THE GRID, because that is where the eye is. Filling rows moves you down
                  * the page, and the button used to sit up top - beside the header fields on the
                  * brand side, in the page header on the site's own portal - so after the last
                  * row people looked below for it and found footer text. Both modes now end
                  * here: what happens on submit, beside the button that submits it.
                  */}
                <Group gap={12} wrap="nowrap">
                    {problemCount > 0 && (
                        <Text fz={12} fw={600} c={semanticColors.critical.dark}>
                            Fix {problemCount} row{problemCount === 1 ? '' : 's'} to save
                        </Text>
                    )}
                    <Button
                        variant={ButtonVariant.ACTION}
                        leftSection={<Plus size={14} />}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        loading={isSubmitting}
                    >
                        Record delivery and QC
                    </Button>
                </Group>
            </div>

            {/*
              * ONE QUESTION, ASKED ONCE, BEFORE THE WRITE. The gate used to refuse a quantity
              * over the order outright, and the owner then had to amend the order somewhere
              * else and come back. On the brand's door the extra is instead priced here, in the
              * same breath as the receipt - the inFlow shape - and the order gets a line for it.
              */}
            <Modal
                opened={overPrompt !== null}
                onClose={() => setOverPrompt(null)}
                title="More than ordered"
                size="lg"
                centered
            >
                {overPrompt && (
                    <Stack gap="md">
                        <Text fz={13} c={neutralColors.textSecondary}>
                            This delivery brings {order?.orderNumber} above what it asked for. Nothing is saved
                            yet. Say what happens to the extra and it goes in with the rest; the order gets a
                            line for it, and the line as issued stays exactly as it was.
                        </Text>
                        {overItems.map((item) => (
                            <Group key={item.itemId} justify="space-between" align="flex-start" wrap="wrap" gap={12}>
                                <div>
                                    <Text fz={13} fw={600}>
                                        {item.name}
                                    </Text>
                                    <Text fz={12} c={neutralColors.textSecondary}>
                                        {`Ordered ${formatCount(item.ordered)} ${item.unit} · ${formatCount(item.remaining)} ${item.unit} still owed · this delivery is ${formatCount(item.over)} ${item.unit} over`.replace(
                                            /\s+/g,
                                            ' ',
                                        )}
                                        {item.unitPrice != null &&
                                            ` · rate on the order ${new Intl.NumberFormat('en-IN', {
                                                style: 'currency',
                                                currency: draft.currency || 'INR',
                                            }).format(item.unitPrice)}`}
                                    </Text>
                                </div>
                                <SegmentedControl
                                    size="xs"
                                    value={overPrompt[item.itemId]}
                                    onChange={(value) =>
                                        setOverPrompt({ ...overPrompt, [item.itemId]: value as OverDeliveryDecision })
                                    }
                                    data={[
                                        { value: 'PAY', label: 'Pay for them' },
                                        { value: 'FREE', label: 'Receive free' },
                                    ]}
                                />
                            </Group>
                        ))}
                        <Text fz={12} c={neutralColors.textMuted}>
                            Pay for them adds a line for the extra at the same rate as the line it tops up.
                            Receive free adds the same line at nothing. Cancel saves nothing; change the
                            quantity to what the order allows and the rest goes back with the driver.
                        </Text>
                        <Group justify="flex-end" gap={8}>
                            <Button variant={ButtonVariant.DEFAULT} onClick={() => setOverPrompt(null)}>
                                Cancel
                            </Button>
                            <Button variant={ButtonVariant.ACTION} onClick={confirmOverDelivery} loading={isSubmitting}>
                                Receive and amend the order
                            </Button>
                        </Group>
                    </Stack>
                )}
            </Modal>

            {addSupplierMaterial && addSupplierFor && (
                <AddSupplierModal
                    key={addSupplierFor.rowId}
                    material={addSupplierMaterial}
                    suppliers={catalog?.suppliers ?? []}
                    initialVendorId={addSupplierFor.vendorId}
                    initialSearch={addSupplierFor.search}
                    onClose={() => setAddSupplierFor(null)}
                    onAdded={(vendorId) => updateRow(addSupplierFor.rowId, { supplierId: vendorId })}
                />
            )}
        </div>
    );
};

export default VendorReceiveContainer;
